/**
 * Trigger a browser download of `blob` as a file named `fileName`.
 *
 * Wraps the blob-URL → temporary anchor → click → revoke dance so the lifecycle
 * (and its browser quirks) lives in one place. The revoke is deferred: some browsers
 * (Safari, older Firefox) read the blob asynchronously after `click()`, and revoking
 * it before they finish can abort the download.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** Trigger a browser download of `content` as a file named `fileName`. */
export function downloadTextFile(
  content: string,
  fileName: string,
  mimeType = 'text/html;charset=utf-8',
): void {
  downloadBlob(new Blob([content], { type: mimeType }), fileName)
}

/**
 * Ensure `name` ends with `.${extension}` (case-insensitive), appending it if not.
 * `extension` is given without the leading dot (`'html'`, `'mmd'`).
 */
export function ensureFileExtension(name: string, extension: string): string {
  const suffix = `.${extension}`
  return name.toLowerCase().endsWith(suffix.toLowerCase())
    ? name
    : `${name}${suffix}`
}

/** Ensure `name` ends with `.html` (case-insensitive), appending the extension if not. */
export function ensureHtmlExtension(name: string): string {
  return ensureFileExtension(name, 'html')
}
